// src/pages/CompanyManagement/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { companiesApi, cameraFootageRequestsApi } from '../../services/apiClient';
import CompanyList from './CompanyList';
import CompanyDetails from './CompanyDetails';
import CreateCompanyDialog from './CreateCompanyDialog';
import { usePersistedFilter, FILTER_KEYS } from '../../utils/filterPersistence';

const CompanyManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistedFilter(FILTER_KEYS.COMPANY.SEARCH, '');

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  // Check if user can access company management
  const isAdmin = user?.role === 'admin';
  const canManageCompany = hasPermission('company.settings.manage') || hasPermission('company.edit');
  const hasAccess = isAdmin || canManageCompany;

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);

      // Admins can see all companies, others see only their own
      if (isAdmin) {
        const response = await companiesApi.getAll({ search: searchTerm });

        if (response.data.success) {
          setCompanies(response.data.data);
        }
      } else if (canManageCompany) {
        // Non-admin users with company permissions see only their own company
        const companyId = user?.company?.id;

        if (companyId) {
          const response = await companiesApi.getById(companyId);

          if (response.data.success) {
            setCompanies([response.data.data]);
            // Auto-select the user's company
            setSelectedCompany(response.data.data);
          }
        } else {
          console.warn('[CompanyManagement] No company ID found for user');
          enqueueSnackbar(
            t('CompanyManagement.user_no_company'),
            { variant: 'warning' }
          );
        }
      }
    } catch (error) {
      console.error('[CompanyManagement] Error loading companies:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_load'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [searchTerm, enqueueSnackbar, isAdmin, canManageCompany, user, t]);

  useEffect(() => {
    if (hasAccess) {
      loadCompanies();
    }
  }, [hasAccess, loadCompanies]);

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
  };

  const handleCompanyCreated = (newCompany) => {
    setOpenCreateDialog(false);
    enqueueSnackbar(t('CompanyManagement.success_created'), { variant: 'success' });
    loadCompanies();
    setSelectedCompany(newCompany);
  };

  const handleCompanyUpdated = () => {
    loadCompanies();
    if (selectedCompany) {
      // Reload selected company details
      loadCompanyDetails(selectedCompany.id);
    }
  };

  const loadCompanyDetails = async (companyId) => {
    try {
      const response = await companiesApi.getById(companyId);

      if (response.data.success) {
        setSelectedCompany(response.data.data);
      }
    } catch (error) {
      console.error('Error loading company details:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_load_details'),
        { variant: 'error' }
      );
    }
  };

  const handleCompanyDeleted = () => {
    setSelectedCompany(null);
    loadCompanies();
    enqueueSnackbar(t('CompanyManagement.success_deleted'), { variant: 'success' });
  };

  const handleExportPDF = async () => {
    try {
      setExportingPdf(true);

      const response = await cameraFootageRequestsApi.exportPDF();

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'camera-requests-audit-report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        t('CompanyManagement.pdf_export_success') || 'PDF audit report exported successfully',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.pdf_export_error') || 'Failed to export PDF audit report',
        { variant: 'error' }
      );
    } finally {
      setExportingPdf(false);
    }
  };

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('CompanyManagement.no_permission')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Company List */}
      <Box
        sx={{
          width: 350,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.title')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreateDialog(true)}
                  fullWidth
                  size="small"
                >
                  {t('CompanyManagement.new_company')}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={loadCompanies}
                disabled={loading}
                size="small"
                {...(isAdmin ? {} : { fullWidth: true })}
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Company List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <CompanyList
            companies={companies}
            selectedCompany={selectedCompany}
            onSelectCompany={handleCompanySelect}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            t={t}
          />
        )}
      </Box>

      {/* Right Panel - Company Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedCompany ? (
          <CompanyDetails
            company={selectedCompany}
            onUpdate={handleCompanyUpdated}
            onDelete={handleCompanyDeleted}
            t={t}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3
            }}
          >
            <BusinessIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('CompanyManagement.select_company')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('CompanyManagement.select_company_subtitle')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Company Dialog - Admin only */}
      {isAdmin && (
        <CreateCompanyDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          onSuccess={handleCompanyCreated}
          t={t}
        />
      )}
    </Box>
  );
};

export default CompanyManagement;
